/**
 * @fileoverview Game Timer Component
 * @description Displays elapsed time during the game
 */

import React, { memo } from "react";
import { View, Text, useColorScheme } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { formatTime } from "../utils/sudokuValidator";

interface GameTimerProps {
  /** Time in seconds */
  seconds: number;
  /** Whether the timer is paused */
  isPaused?: boolean;
  /** Size variant */
  size?: "small" | "medium" | "large";
}

/**
 * Timer display component
 */
export const GameTimer = memo<GameTimerProps>(
  ({ seconds, isPaused = false, size = "medium" }) => {
    const colorScheme = useColorScheme();
    const isDark = colorScheme === "dark";

    const sizeConfig = {
      small: { iconSize: 16, fontSize: 16, gap: 4 },
      medium: { iconSize: 20, fontSize: 20, gap: 6 },
      large: { iconSize: 28, fontSize: 28, gap: 8 },
    };

    const { iconSize, fontSize, gap } = sizeConfig[size];

    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap,
          opacity: isPaused ? 0.5 : 1,
        }}
        accessibilityRole="timer"
        accessibilityLabel={`Tijd: ${formatTime(seconds)}`}
      >
        <Ionicons
          name={isPaused ? "pause-circle-outline" : "time-outline"}
          size={iconSize}
          color={isDark ? "#9CA3AF" : "#6B7280"}
        />
        <Text
          style={{
            fontSize,
            fontWeight: "600",
            fontVariant: ["tabular-nums"],
            color: isDark ? "#F9FAFB" : "#111827",
          }}
        >
          {formatTime(seconds)}
        </Text>
      </View>
    );
  }
);

GameTimer.displayName = "GameTimer";

